import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Clock, ExternalLink, Eye, Mail, Plus, Trash2, UserMinus } from 'lucide-react'
import PageContainer from '@/components/layout/PageContainer'
import Card from '@/components/layout/Card'
import TableCard from '@/components/layout/TableCard'
import Select from '@/components/ui/Select'
import TimeSelect from '@/components/ui/TimeSelect'
import {
  useCancelEnrollment,
  useCreateDripSequence,
  useDripEnrollments,
  useDripSequences,
  useEnrollInDrip,
  useUpdateDripSequence,
} from '@/features/drip/useDrip'
import { cadenceText, delayText } from '@/features/drip/sequenceText'
import { useLists } from '@/features/lists/useLists'
import { useTemplates } from '@/features/messaging/useMessaging'
import { MESSAGE_TOKENS, insertTokenAt } from '@/features/messaging/tokens'
import InsertTokenMenu from '@/components/ui/InsertTokenMenu'
import IndexToolbar from '@/components/IndexToolbar'
import { matchesQuery } from '@/lib/matchesQuery'
import { useAuthStore } from '@/features/auth/authStore'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import { formatShortDate } from '@/lib/dealFormat'
import { cn } from '@/lib/cn'
import { DELAY_UNITS, splitDelay, stepDelayMinutes, type DripSequence, type DripStep } from '@/api/drip'

const inputCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

/** Tokens offered in a step; the signature is added server-side, so it isn't insertable here. */
const STEP_TOKENS = MESSAGE_TOKENS.filter((t) => t !== '{{agent_signature}}')

interface Draft {
  name: string
  enabled: boolean
  steps: DripStep[]
  windowStart: string
  windowEnd: string
}

function draftOf(s?: DripSequence): Draft {
  return {
    name: s?.name ?? '',
    enabled: s?.enabled ?? true,
    steps: s?.steps.length ? s.steps.map((x) => ({ ...x })) : [{ delayMinutes: 0, subject: '', body: '' }],
    windowStart: (s?.sendWindowStart ?? '').slice(0, 5),
    windowEnd: (s?.sendWindowEnd ?? '').slice(0, 5),
  }
}

/** A step's copy with the tokens filled from a sample recipient — what one client would read. */
function sampleText(text: string, agentName: string): string {
  const values: Record<string, string> = {
    first_name: 'Jane',
    last_name: 'Smith',
    deal_title: '12 Example Street',
    agent_name: agentName,
    agent_email: '',
    agent_phone: '',
    email: 'jane@example.com',
  }
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k: string) => values[k] ?? '')
}

type Tab = 'emails' | 'contacts'

/**
 * One sequence on its own page: the emails as a timeline (when each goes, subject and text, a preview
 * with a sample client's name), sending hours and enrolling on the side, and a Contacts tab showing who
 * is in it, how far they've got, and taking someone out.
 */
export default function SequenceEditorPage() {
  const { id } = useParams()
  const isNew = !id
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const tab: Tab = params.get('tab') === 'contacts' && !isNew ? 'contacts' : 'emails'
  const { data: sequences, isLoading } = useDripSequences()
  const sequence = sequences?.find((s) => s.id === id)
  const { data: templates } = useTemplates('email')
  const { data: lists } = useLists()
  const { data: allEnrollments } = useDripEnrollments(isNew ? undefined : id)
  const [personQuery, setPersonQuery] = useState('')
  const enrollments = (allEnrollments ?? []).filter((e) => matchesQuery(personQuery, e.contactName, e.email))
  const create = useCreateDripSequence()
  const update = useUpdateDripSequence()
  const enroll = useEnrollInDrip()
  const cancel = useCancelEnrollment(id ?? '')
  const save = isNew ? create : update
  const agentName = useAuthStore((s) => s.user?.fullName) ?? 'Your name'

  const [draft, setDraft] = useState<Draft>(() => draftOf())
  const [baseline, setBaseline] = useState(() => JSON.stringify(draftOf()))
  const [loadedId, setLoadedId] = useState<string | null>(null)
  if (sequence && sequence.id !== loadedId) {
    setLoadedId(sequence.id)
    setDraft(draftOf(sequence))
    setBaseline(JSON.stringify(draftOf(sequence)))
  }
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }))
  const setStep = (i: number, patch: Partial<DripStep>) => set('steps', draft.steps.map((s, j) => (j === i ? { ...s, ...patch } : s)))
  const dirty = JSON.stringify(draft) !== baseline
  const [previewing, setPreviewing] = useState<number | null>(null)
  const [listId, setListId] = useState('')
  const [enrolled, setEnrolled] = useState<number | null>(null)

  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  const problems = [
    !draft.name.trim() && 'Give the sequence a name.',
    draft.steps.some((s) => !s.templateId && !s.body?.trim()) && 'Every email needs some text.',
    !!draft.windowStart !== !!draft.windowEnd && 'Set both sending times, or neither.',
  ].filter(Boolean) as string[]

  function submit() {
    if (problems.length) return
    const input = {
      name: draft.name.trim(),
      enabled: draft.enabled,
      steps: draft.steps,
      sendWindowStart: draft.windowStart && draft.windowEnd ? draft.windowStart : null,
      sendWindowEnd: draft.windowStart && draft.windowEnd ? draft.windowEnd : null,
    }
    if (isNew) create.mutate(input, { onSuccess: (s) => { setBaseline(JSON.stringify(draft)); navigate(`/drip/${s.id}`, { replace: true }) } })
    else if (id) update.mutate({ id, input }, { onSuccess: (s) => { setDraft(draftOf(s)); setBaseline(JSON.stringify(draftOf(s))) } })
  }

  if (!isNew && !isLoading && !sequence) {
    return (
      <PageContainer width="full">
        <p className="text-sm text-gray-500">This sequence doesn't exist or was deleted. <Link to="/drip" className="text-brand-primary hover:underline">Back to sequences</Link></p>
      </PageContainer>
    )
  }

  const status = (e: { status: string; stepsSent: number }) =>
    e.status === 'completed' ? 'Finished' : e.status === 'cancelled' ? 'Removed' : `Email ${Math.min(e.stepsSent + 1, draft.steps.length)} of ${draft.steps.length} next`

  return (
    <PageContainer width="full">
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-gray-500 mb-2">
        <Link to="/drip" className="hover:text-brand-primary">Sequences</Link>
        <span aria-hidden>/</span>
        <span className="text-gray-700 truncate">{isNew ? 'New sequence' : sequence?.name}</span>
      </nav>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <input
          value={draft.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder="Sequence name — e.g. New buyer nurture"
          aria-label="Sequence name"
          className="flex-1 min-w-64 max-w-xl text-2xl font-semibold text-brand-darkest bg-transparent border-b border-transparent hover:border-gray-200 focus:border-brand-primary focus:outline-none py-1"
        />
        <div className="flex items-center gap-2">
          <button type="button" role="switch" aria-checked={draft.enabled} onClick={() => set('enabled', !draft.enabled)}
            className={cn('inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium', draft.enabled ? 'border-success/30 bg-success/10 text-success' : 'border-gray-200 text-gray-500')}>
            <span className={cn('w-2 h-2 rounded-full', draft.enabled ? 'bg-success' : 'bg-gray-300')} />
            {draft.enabled ? 'Live' : 'Paused'}
          </button>
          <button type="button" onClick={submit} disabled={save.isPending || problems.length > 0 || (!isNew && !dirty)} className="px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-60">
            {save.isPending ? 'Saving…' : isNew ? 'Create sequence' : dirty ? 'Save changes' : 'Saved'}
          </button>
        </div>
      </div>
      {save.isError && <p className="text-danger text-sm mb-3">{apiErrorMessage(save.error)}</p>}
      {problems.length > 0 && draft.name.trim() !== '' && <ul className="mb-3 text-xs text-amber-700 space-y-0.5">{problems.map((p) => <li key={p}>• {p}</li>)}</ul>}

      <div className="flex gap-1 border-b border-gray-200 mb-5">
        {(['emails', 'contacts'] as const).filter((t) => t === 'emails' || !isNew).map((t) => (
          <button key={t} type="button" onClick={() => setParams(t === 'emails' ? {} : { tab: t }, { replace: true })}
            className={cn('px-3 py-2 text-sm -mb-px border-b-2', tab === t ? 'border-brand-primary text-brand-primary font-medium' : 'border-transparent text-gray-500 hover:text-brand-dark')}>
            {t === 'emails' ? `Emails · ${draft.steps.length}` : `Contacts · ${(sequence?.activeEnrollments ?? 0) + (sequence?.completedEnrollments ?? 0)}`}
          </button>
        ))}
      </div>

      {tab === 'emails' ? (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="min-w-0 max-w-3xl">
            {draft.steps.map((step, i) => {
              const minutes = stepDelayMinutes(step)
              const { value, unit } = splitDelay(minutes)
              const perUnit = DELAY_UNITS.find((u) => u.value === unit)?.perUnit ?? 1
              return (
                <div key={i}>
                  {i > 0 && <div className="ml-8 h-4 border-l-2 border-gray-200" aria-hidden />}
                  <div className="rounded-xl border border-gray-200 bg-white">
                    <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-100">
                      <span className="w-8 h-8 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center"><Mail size={16} /></span>
                      <span className="text-sm font-semibold text-brand-darkest">Email {i + 1}</span>
                      <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                        <Clock size={14} className="text-gray-400" /> sent
                        <input type="number" min={0} aria-label={`Email ${i + 1} delay`} value={value}
                          onChange={(e) => setStep(i, { delayMinutes: Math.max(0, Number(e.target.value) || 0) * perUnit, dayOffset: undefined })}
                          className="w-16 px-2 py-1 border border-gray-200 rounded-lg text-sm" />
                        <Select size="sm" aria-label={`Email ${i + 1} delay unit`} value={unit}
                          onChange={(e) => { const u = DELAY_UNITS.find((x) => x.value === e.target.value); if (u) setStep(i, { delayMinutes: value * u.perUnit, dayOffset: undefined }) }}>
                          {DELAY_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                        </Select>
                        after enrolling
                      </span>
                      <div className="ml-auto flex items-center gap-1">
                        <button type="button" onClick={() => setPreviewing(previewing === i ? null : i)} className={cn('inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs', previewing === i ? 'bg-brand-primary/10 text-brand-primary' : 'text-gray-500 hover:text-brand-primary')}>
                          <Eye size={14} /> Preview
                        </button>
                        {draft.steps.length > 1 && (
                          <button type="button" onClick={() => set('steps', draft.steps.filter((_, j) => j !== i))} className="p-1.5 text-gray-400 hover:text-danger" title="Remove email"><Trash2 size={15} /></button>
                        )}
                      </div>
                    </div>
                    {previewing === i ? (
                      <div className="p-4">
                        <div className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
                          <div className="px-4 py-2.5 border-b border-gray-200 text-xs text-gray-500">
                            To Jane Smith · <span className="font-semibold text-gray-700">{sampleText(step.subject ?? '', agentName) || '(no subject)'}</span>
                          </div>
                          <div className="px-4 py-3 bg-white text-sm text-gray-700 whitespace-pre-wrap">{sampleText(step.body ?? '', agentName)}</div>
                          <div className="px-4 py-2 bg-white border-t border-gray-100 text-xs text-gray-400">+ the sender’s signature and an unsubscribe link</div>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">Shown for a sample client, Jane Smith. Each real client sees their own name.</p>
                      </div>
                    ) : (
                      <div className="p-4 space-y-3">
                        <Select size="md" aria-label={`Load a template into email ${i + 1}`} value=""
                          onChange={(e) => { const t = (templates ?? []).find((x) => x.id === e.target.value); if (t) setStep(i, { templateId: undefined, subject: t.subject ?? '', body: t.body ?? '' }) }}
                          className="w-full">
                          <option value="">Start from a template…</option>
                          {(templates ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </Select>
                        <input className={inputCls} aria-label={`Email ${i + 1} subject`} placeholder="Subject" value={step.subject ?? ''} onChange={(e) => setStep(i, { subject: e.target.value })} />
                        <textarea id={`seq-step-${i}`} className={inputCls} rows={6} aria-label={`Email ${i + 1} text`} placeholder="Write the email…" value={step.body ?? ''} onChange={(e) => setStep(i, { body: e.target.value })} />
                        <div>
                          <InsertTokenMenu
                            tokens={STEP_TOKENS}
                            onInsert={(t) => {
                              const el = document.getElementById(`seq-step-${i}`) as HTMLTextAreaElement | null
                              const { next, caret } = insertTokenAt(el, step.body ?? '', t)
                              setStep(i, { body: next })
                              requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(caret, caret) })
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
            <div className="ml-8 h-4 border-l-2 border-gray-200" aria-hidden />
            <button type="button"
              onClick={() => set('steps', [...draft.steps, { delayMinutes: stepDelayMinutes(draft.steps[draft.steps.length - 1] ?? {}) + 3 * 24 * 60, subject: '', body: '' }])}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-brand-primary/40 text-sm font-medium text-brand-primary hover:bg-brand-primary/5">
              <Plus size={15} /> Add an email
            </button>
          </div>

          <aside className="space-y-4 xl:sticky xl:top-6 self-start">
            <Card title="Schedule">
              <ol className="space-y-1.5 text-sm text-gray-700">
                {draft.steps.map((s, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="w-5 h-5 shrink-0 rounded-full bg-brand-primary/10 text-brand-primary text-[11px] font-semibold flex items-center justify-center">{i + 1}</span>
                    <span className="min-w-0">
                      <span className="text-gray-500">{stepDelayMinutes(s) <= 0 ? 'Straight away' : `After ${delayText(stepDelayMinutes(s))}`}: </span>
                      <span className="truncate">{s.subject || '(no subject)'}</span>
                    </span>
                  </li>
                ))}
              </ol>
              <p className="text-xs text-gray-400 mt-2">{cadenceText(draft.steps)}, counted from when each contact is enrolled.</p>
            </Card>
            <Card title="Sending hours">
              <div className="flex items-center gap-2">
                <TimeSelect aria-label="Send from" size="md" emptyLabel="Any time" value={draft.windowStart} onChange={(v) => set('windowStart', v)} />
                <span className="text-xs text-gray-400">to</span>
                <TimeSelect aria-label="Send until" size="md" emptyLabel="Any time" value={draft.windowEnd} onChange={(v) => set('windowEnd', v)} />
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {draft.windowStart && draft.windowEnd ? 'Emails due outside these hours wait for the next opening.' : 'Blank = send as soon as each email is due.'} In your
                agency’s timezone (Settings → Branding).
                {(draft.windowStart || draft.windowEnd) && (
                  <button type="button" onClick={() => setDraft((d) => ({ ...d, windowStart: '', windowEnd: '' }))} className="ml-1 text-brand-primary">Clear</button>
                )}
              </p>
            </Card>
            {!isNew && (
              <Card title="Enrol a list">
                <div className="flex gap-2">
                  <Select size="md" aria-label="List to enrol" value={listId} onChange={(e) => { setListId(e.target.value); setEnrolled(null) }} className="flex-1 min-w-0">
                    <option value="">Choose a list…</option>
                    {(lists ?? []).map((l) => <option key={l.id} value={l.id}>{l.name} · {l.memberCount}</option>)}
                  </Select>
                  <button type="button" disabled={!listId || enroll.isPending || dirty}
                    onClick={() => enroll.mutate({ id: id!, input: { listId } }, { onSuccess: (r) => { setEnrolled(r.enrolled); setListId('') } })}
                    className="px-3 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-60">
                    Enrol
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2" aria-live="polite">
                  {dirty ? 'Save your changes first.' : enrolled != null ? (enrolled === 0 ? 'Everyone on that list is already in this sequence.' : `${enrolled} contact${enrolled === 1 ? '' : 's'} enrolled.`) : 'People already in the sequence are skipped. To enrol individuals, tick them on Contacts → “Enrol in sequence”.'}
                </p>
                {enroll.isError && <p className="text-danger text-xs mt-1">{apiErrorMessage(enroll.error)}</p>}
              </Card>
            )}
          </aside>
        </div>
      ) : (
        <>
        {(allEnrollments ?? []).length > 5 && (
          <IndexToolbar query={personQuery} onQueryChange={setPersonQuery} placeholder="Search people in this sequence…" shown={enrollments.length} total={(allEnrollments ?? []).length} />
        )}
        <TableCard>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-gray-500 border-b border-gray-100 bg-gray-50/60">
                <th className="px-4 py-2.5">Contact</th>
                <th className="px-3 py-2.5">Progress</th>
                <th className="px-3 py-2.5">Next email</th>
                <th className="px-3 py-2.5">Enrolled</th>
                <th className="px-3 py-2.5 w-24"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {enrollments.map((e) => (
                <tr key={e.id} className="border-b border-gray-50 last:border-0">
                  <td className="px-4 py-2.5">
                    <a href={`/contacts/${e.contactId}`} target="_blank" rel="noreferrer" className="font-medium text-brand-darkest hover:text-brand-primary inline-flex items-center gap-1">
                      {e.contactName} <ExternalLink size={12} className="text-gray-300" />
                    </a>
                    {e.email && <div className="text-xs text-gray-500">{e.email}</div>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full', e.status === 'active' ? 'bg-brand-primary/10 text-brand-primary' : e.status === 'completed' ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-500')}>
                      {status(e)}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                    {/* the day only: the exact time shifts into the sending hours when they're set */}
                    {e.status === 'active' ? formatShortDate(e.nextRunAt) : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">{formatShortDate(e.enrolledAt)}</td>
                  <td className="px-3 py-2.5 text-right">
                    {e.status === 'active' && (
                      <button type="button" onClick={() => cancel.mutate(e.id)} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-danger" title={`Stop sending to ${e.contactName}`}>
                        <UserMinus size={14} /> Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(allEnrollments ?? []).length === 0 && (
            <p className="px-4 py-10 text-center text-sm text-gray-500">Nobody is in this sequence yet — enrol a list from the Emails tab, or tick contacts on Contacts → “Enrol in sequence”.</p>
          )}
          {(allEnrollments ?? []).length > 0 && enrollments.length === 0 && (
            <p className="px-4 py-10 text-center text-sm text-gray-500">Nobody in this sequence matches “{personQuery}”.</p>
          )}
        </TableCard>
        </>
      )}
    </PageContainer>
  )
}
